import React from "react";
import { Box, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import ReviewHorizontalCarousel from "./ReviewHorizontalCarousel";
import {
  NoticeComposerAttachmentThumb,
  type NoticeComposerUploadedFile,
} from "./NoticeComposerAttachmentThumb";
import { NoticeUploadProgress } from "../shared/NoticeUploadProgress";
import { isDriveVideoFile } from "../../utils/driveFolder";
import { lightTheme } from "../../styles/theme";
import type { NoticeUploadProgressState } from "../../utils/presignedUpload";

const getFileExtension = (fileName: string) => {
  const parts = fileName.split(".");
  if (parts.length < 2) return "FILE";
  return parts[parts.length - 1].toUpperCase();
};

export type NoticeComposerAttachmentsCarouselProps = {
  files: NoticeComposerUploadedFile[];
  onRemove?: (index: number) => void;
  isUploading?: boolean;
  uploadStatusMessage?: string;
  uploadProgress?: NoticeUploadProgressState | null;
  onCancelUpload?: () => void;
};

const NoticeComposerAttachmentsCarousel: React.FC<
  NoticeComposerAttachmentsCarouselProps
> = ({
  files,
  onRemove,
  isUploading,
  uploadStatusMessage,
  uploadProgress,
  onCancelUpload,
}) => {
  if (files.length === 0) return null;

  return (
    <Box sx={{ mt: 1.5, mb: 1.5, position: "relative" }}>
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 600, mb: 1, color: "text.primary" }}
      >
        Attachments ({files.length})
      </Typography>

      <ReviewHorizontalCarousel
        contentKey={files.map((f) => f.fileUrl).join("|")}
      >
        <Box
          sx={{
            display: "flex",
            gap: 1.1,
            minWidth: "max-content",
            px: 0.2,
            py: 0.25,
          }}
        >
          {files.map((file, index) => {
            const isMedia =
              file.contentType?.startsWith("image/") ||
              isDriveVideoFile(file.contentType, file.fileName);

            return (
              <Box
                key={`${file.fileUrl}-${index}`}
                sx={{
                  width: 72,
                  flexShrink: 0,
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: 72,
                    height: 72,
                    borderRadius: 1.5,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "grey.50",
                  }}
                >
                  {isMedia ? (
                    <NoticeComposerAttachmentThumb
                      file={file}
                      variant="tile"
                      showPlayHint={false}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.5,
                      }}
                    >
                      <DescriptionIcon
                        sx={{
                          color: lightTheme.colors.materialBlue,
                          fontSize: 28,
                        }}
                      />
                      <Box
                        sx={{
                          px: 0.6,
                          py: 0.1,
                          borderRadius: 0.75,
                          bgcolor: lightTheme.colors.materialBlue,
                          color: "common.white",
                          fontSize: "0.58rem",
                          fontWeight: 700,
                          maxWidth: "80%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {getFileExtension(file.fileName)}
                      </Box>
                    </Box>
                  )}

                  {isUploading && index === 0 ? (
                    <NoticeUploadProgress
                      variant="overlay"
                      progress={uploadProgress ?? null}
                      statusMessage={uploadStatusMessage}
                      onCancel={onCancelUpload}
                    />
                  ) : null}

                  {onRemove ? (
                    <IconButton
                      size="small"
                      onClick={() => onRemove(index)}
                      disabled={isUploading}
                      aria-label={`Remove ${file.fileName}`}
                      sx={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        width: 22,
                        height: 22,
                        bgcolor: "rgba(0,0,0,0.55)",
                        color: "common.white",
                        zIndex: 2,
                        "&:hover": { bgcolor: "rgba(0,0,0,0.72)" },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  ) : null}
                </Box>

                <Typography
                  variant="caption"
                  sx={{
                    mt: 0.45,
                    display: "block",
                    color: "text.secondary",
                    fontSize: "0.68rem",
                    lineHeight: 1.25,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={file.fileName}
                >
                  {file.fileName}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </ReviewHorizontalCarousel>
    </Box>
  );
};

export default NoticeComposerAttachmentsCarousel;
